import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs/Observable";
import { map } from "rxjs/operators";

import { environment } from "./../../../environments/environment";
import {
  OcrRecord,
  OCR_IDENTITY_TO_FACE_FIELD
} from "../../models/ocr.model";

/**
 * Talks to the CRM's OCR proxy, which forwards to the OCR pipeline over a
 * private WireGuard tunnel. Nothing here reaches the pipeline directly.
 *
 * Ported from the VIE frontend, with three deliberate differences forced by
 * this project's toolchain:
 *
 *   - TypeScript 3.2 has no optional chaining or nullish coalescing, so every
 *     access is guarded explicitly rather than with `?.` / `??`.
 *   - RxJS 5.5 has no `throwError`, so "no record in the response" is
 *     signalled by returning null instead of erroring, and the caller decides.
 *   - AuthInterceptor here used to set Content-Type unconditionally, which
 *     breaks multipart. It now skips it for FormData; do not set it here.
 */
@Injectable()
export class OcrService {
  baseUrl: string = environment.api_base_url;

  /** The synchronous endpoint reads at most two images. */
  readonly maxFiles = 2;

  constructor(public http: HttpClient) {}

  /**
   * Send the front and (optionally) back of one card. Emits the merged record
   * for that person, or null when the response held no ID record.
   *
   * Send the back whenever it is available: the number is printed there as one
   * continuous run rather than the widely spaced groups on the front, which
   * makes it the more reliable face for the field everything else derives from.
   */
  scanDocuments(files: File[]): Observable<OcrRecord> {
    const payload = new FormData();
    const usable = [];
    for (let i = 0; i < files.length && usable.length < this.maxFiles; i++) {
      if (files[i]) {
        usable.push(files[i]);
      }
    }
    for (let i = 0; i < usable.length; i++) {
      payload.append("files[]", usable[i], usable[i].name);
    }

    return this.http
      .post(this.baseUrl + "ocr/documents/process", payload)
      .pipe(map((response: any) => this.primaryRecord(response)));
  }

  /**
   * Pick the record to work with, tolerating the shapes the proxy has been
   * seen to return: the full { request_id, records: [...] } envelope, a bare
   * array of records, or a single record. Passport-only records are skipped -
   * these forms are about national IDs - and where a mismatched pair produced
   * two records the fuller one is the real card.
   */
  primaryRecord(response: any): OcrRecord {
    if (!response) {
      return null;
    }

    let records: OcrRecord[] = [];
    if (Array.isArray(response)) {
      records = response;
    } else if (response.records && Array.isArray(response.records)) {
      records = response.records;
    } else if (response.identity) {
      records = [response as OcrRecord];
    }

    const cards: OcrRecord[] = [];
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      const key = record && record.key ? String(record.key) : "";
      if (record && key.indexOf("passport:") !== 0) {
        cards.push(record);
      }
    }
    if (!cards.length) {
      return null;
    }

    let best = cards[0];
    for (let i = 1; i < cards.length; i++) {
      if (this.identitySize(cards[i]) > this.identitySize(best)) {
        best = cards[i];
      }
    }
    return best;
  }

  /**
   * Whether something independent confirmed this field.
   *
   * `identity` carries the consolidated value but not its proof, so the answer
   * lives in the face the value came from - and the field is often named
   * differently there (`full_name_ar` in identity is `full_name` on the face).
   *
   * This deliberately does not fall back to a confidence threshold. A national
   * number whose check digit verified arrives at about 0.73 while an
   * unverifiable Arabic name can sit at 0.12; a threshold would call the proven
   * number unconfirmed and rank the two by the wrong property entirely.
   */
  isValidated(record: OcrRecord, identityKey: string): boolean {
    const entry = this.identityEntry(record, identityKey);
    if (!entry || entry.value === null || entry.value === undefined) {
      return false;
    }

    const from = entry.from;
    if (from === "id_front") {
      return this.faceValidated(record.id_front, identityKey);
    }
    if (from === "id_back") {
      return this.faceValidated(record.id_back, identityKey);
    }
    // gender and governorate are derived from the number and reported with
    // from: "national_number"; they are proven when that number was.
    if (from === "national_number" && identityKey !== "national_number") {
      return this.isValidated(record, "national_number");
    }
    // Unknown provenance: look on both faces rather than assume.
    return (
      this.faceValidated(record.id_front, identityKey) ||
      this.faceValidated(record.id_back, identityKey)
    );
  }

  /** Read, but nothing independent confirms it - show it, do not trust it. */
  needsVerification(record: OcrRecord, identityKey: string): boolean {
    const entry = this.identityEntry(record, identityKey);
    if (!entry || entry.value === null || entry.value === undefined) {
      return false;
    }
    return !this.isValidated(record, identityKey);
  }

  /**
   * The national number is the only field worth keying another record off, and
   * only when the pipeline confirmed it. Returns null otherwise, so a caller
   * cannot accidentally pick somebody's lead from a guess.
   */
  verifiedNationalNumber(record: OcrRecord): string {
    if (!this.isValidated(record, "national_number")) {
      return null;
    }
    const entry = this.identityEntry(record, "national_number");
    const digits = String(entry && entry.value ? entry.value : "").replace(/\D/g, "");
    return digits.length === 14 ? digits : null;
  }

  /** The plain value of one identity field, or null. */
  identityValue(record: OcrRecord, identityKey: string): string {
    const entry = this.identityEntry(record, identityKey);
    if (!entry || entry.value === null || entry.value === undefined) {
      return null;
    }
    const text = String(entry.value).trim();
    return text ? text : null;
  }

  /** Flags an operator must act on before the result can be relied upon. */
  criticalFlags(record: OcrRecord): string[] {
    const messages: string[] = [];
    const flags = record && record.flags ? record.flags : [];
    for (let i = 0; i < flags.length; i++) {
      if (flags[i] && flags[i].severity === "critical") {
        messages.push(flags[i].message);
      }
    }
    return messages;
  }

  private identityEntry(record: OcrRecord, identityKey: string): any {
    if (!record || !record.identity) {
      return null;
    }
    return record.identity[identityKey];
  }

  private identitySize(record: OcrRecord): number {
    if (!record || !record.identity) {
      return 0;
    }
    return Object.keys(record.identity).length;
  }

  private faceValidated(face: any, identityKey: string): boolean {
    if (!face) {
      return false;
    }
    const faceKey = OCR_IDENTITY_TO_FACE_FIELD[identityKey] || identityKey;
    const field = face[faceKey];
    return !!field && field.validated === true;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/ocr/ocr.service.ts